import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import ServiceDialog from "@/components/ServiceDialog";
import SectorsSection from "@/components/home/SectorsSection";
import FaqSection from "@/components/FaqSection";
import FloatingBulb from "@/components/FloatingBulb";
import { Reveal } from "@/components/motion";
import { SERVICES } from "@/lib/services";
import { FAQS } from "@/lib/faq";
import { useSeo, SITE_URL } from "@/lib/seo";

const SERVIZI_FAQ_SLUGS = [
  "come-funziona-finanza-agevolata",
  "zes-unica-crediti-imposta-mezzogiorno",
  "capire-se-azienda-guadagna",
  "assumere-primo-dipendente-costi",
  "formazione-personale-fondi-interprofessionali",
  "marketing-ristorante-struttura-ricettiva",
];
const SERVIZI_FAQS = SERVIZI_FAQ_SLUGS.map((s) => FAQS.find((f) => f.slug === s));

const SERVIZI_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ItemList",
      name: "Servizi Idea 360 Consulting",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.name,
          description: s.long,
          serviceType: s.name,
          provider: { "@id": `${SITE_URL}/#organization` },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Campania" },
            { "@type": "Country", name: "Italia" },
          ],
        },
      })),
    },
    {
      "@type": "FAQPage",
      mainEntity: SERVIZI_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function Servizi() {
  const [selected, setSelected] = useState(null);
  useSeo({
    title: "Servizi di consulenza per imprese e PMI | Idea 360 Consulting",
    description:
      "Undici aree di consulenza: fractional management, finanza, controllo di gestione, finanza agevolata e bandi, marketing, HR, organizzazione, legale, consulenza del lavoro, formazione e contabilità.",
    path: "/servizi",
    jsonLd: SERVIZI_JSONLD,
  });

  return (
    <>
      <section className="relative">
        <FloatingBulb
          size={32}
          className="absolute top-24 right-[10%] hidden md:block"
          testId="servizi-bulb-1"
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-10 sm:pt-28 sm:pb-12">
          <p className="eyebrow mb-5">Servizi</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
            data-testid="servizi-h1"
          >
            Servizi di consulenza per <span className="text-[#FACC15]">imprese, PMI e startup</span>
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-3xl leading-relaxed">
            Undici aree di consulenza coordinate da un unico referente: fractional management, finanza, controllo di
            gestione, finanza agevolata e bandi, marketing, HR, organizzazione, consulenza
            legale, consulenza del lavoro, formazione finanziata e contabilità. Operiamo a
            Napoli, Salerno, Caserta, Avellino, Benevento e in tutta Italia.
          </p>
          <p className="mt-4 text-white/55 text-sm max-w-2xl">
            Clicca su un'area per scoprire cosa comprende e richiedere informazioni: il servizio
            scelto verrà associato alla tua richiesta.
          </p>
        </div>
      </section>

      <section className="pb-16 sm:pb-32 relative">
        <FloatingBulb
          size={24}
          className="absolute top-10 left-[6%] hidden lg:block"
          style={{ animationDelay: "-1.5s" }}
          testId="servizi-bulb-2"
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <ServiceCard service={s} index={i} onSelect={setSelected} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ServiceDialog service={selected} onClose={() => setSelected(null)} />

      <SectorsSection
        eyebrow="Clienti"
        title={<>I clienti che si sono <span className="text-[#FACC15]">affidati a noi</span></>}
        intro="Imprese di settori diversi, con la stessa esigenza: un partner che coordini tutte le aree e resti al loro fianco nell'esecuzione."
      />

      <FaqSection
        items={SERVIZI_FAQS}
        compact
        eyebrow="Domande frequenti"
        title="Domande frequenti sui nostri servizi"
      />

      {/* CTA */}
      <section className="py-14 sm:py-28 border-t border-[#141414]">        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.025em" }}
          >
            Non sai da dove cominciare?
          </h2>
          <p className="text-white/65 text-base mb-8">
            Chiamaci o scrivici: studieremo insieme la combinazione di servizi giusta per te.
          </p>
          <Link
            to="/contatti"
            className="btn-yellow inline-flex items-center gap-2"
            data-testid="servizi-cta-final"
          >
            Richiedi una consulenza
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
